import React, { useState, useEffect, useCallback } from "react";
import {
  listProductFieldDefinitions,
  createProductFieldDefinition,
  updateProductFieldDefinition,
  deleteProductFieldDefinition,
} from "../services/api";
import { friendlyError } from "../utils/friendlyError";
import { t } from "../i18n";

function parseOptions(text) {
  if (!text || !String(text).trim()) return [];
  return String(text)
    .split(/\r?\n/)
    .map((s) => s.trim())
    .filter(Boolean);
}

/**
 * Field key derived from the display label: lowercase, words joined with "_" (e.g. "Batch Number" → batch_number).
 * Must start with a letter (backend rule); non-Latin labels fall back to "field". Made unique against `taken`.
 */
export function keyFromLabel(label, taken = []) {
  let base = String(label || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
  if (!base) base = "field";
  if (!/^[a-z]/.test(base)) base = `f_${base}`;
  base = base.slice(0, 60);
  const used = new Set(taken.map((k) => String(k).toLowerCase()));
  let key = base;
  for (let n = 2; used.has(key); n += 1) key = `${base}_${n}`;
  return key;
}

export function ProductFieldDefinitionsPanel({ merchantId, onChanged }) {
  const [defs, setDefs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    fieldKey: "",
    label: "",
    fieldType: "TEXT",
    optionsText: "",
    sortOrder: 0,
  });

  const refresh = useCallback(async () => {
    if (!merchantId) return;
    setLoading(true);
    setError("");
    try {
      const list = await listProductFieldDefinitions(merchantId);
      setDefs(list);
      onChanged?.();
    } catch (err) {
      setError(friendlyError(err, t("fields.load_failed")));
    } finally {
      setLoading(false);
    }
  }, [merchantId, onChanged]);

  useEffect(() => {
    refresh().catch(() => {});
  }, [refresh]);

  function resetForm() {
    setEditingId(null);
    setForm({
      fieldKey: "",
      label: "",
      fieldType: "TEXT",
      optionsText: "",
      sortOrder: 0,
    });
  }

  function startEdit(d) {
    setEditingId(d.id);
    setForm({
      fieldKey: d.fieldKey,
      label: d.label,
      fieldType: d.fieldType === "SELECT" ? "SELECT" : "TEXT",
      optionsText: (d.options || []).join("\n"),
      sortOrder: d.sortOrder ?? 0,
    });
  }

  async function submit(e) {
    e.preventDefault();
    setError("");
    const label = form.label.trim();
    if (!label) {
      setError(t("fields.label_required"));
      return;
    }
    if (!editingId) {
      if (form.fieldType === "SELECT") {
        const opts = parseOptions(form.optionsText);
        if (opts.length === 0) {
          setError(t("fields.options_required"));
          return;
        }
      }
    } else {
      if (form.fieldType === "SELECT") {
        const opts = parseOptions(form.optionsText);
        if (opts.length === 0) {
          setError(t("fields.options_required"));
          return;
        }
      }
    }

    try {
      if (editingId) {
        const opts = form.fieldType === "SELECT" ? parseOptions(form.optionsText) : [];
        await updateProductFieldDefinition(merchantId, editingId, {
          fieldKey: form.fieldKey.trim(),
          label,
          fieldType: form.fieldType,
          options: form.fieldType === "SELECT" ? opts : [],
          sortOrder: Number(form.sortOrder) || 0,
        });
      } else {
        const key = keyFromLabel(label, defs.map((d) => d.fieldKey));
        const opts = form.fieldType === "SELECT" ? parseOptions(form.optionsText) : [];
        await createProductFieldDefinition(merchantId, {
          fieldKey: key,
          label,
          fieldType: form.fieldType,
          options: form.fieldType === "SELECT" ? opts : [],
          sortOrder: Number(form.sortOrder) || 0,
        });
      }
      resetForm();
      await refresh();
    } catch (err) {
      setError(friendlyError(err, t("store.save_failed")));
    }
  }

  async function remove(id) {
    if (!window.confirm(t("fields.remove_confirm"))) {
      return;
    }
    setError("");
    try {
      await deleteProductFieldDefinition(merchantId, id);
      if (editingId === id) resetForm();
      await refresh();
    } catch (err) {
      setError(friendlyError(err, t("catalog.delete_failed")));
    }
  }

  const sortedDefs = [...defs].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));

  return (
    <div className="fields-layout">
      <section className="fields-layout__list">
        <p className="muted" style={{ margin: "0 0 14px", fontSize: 13.5, lineHeight: 1.55 }}>
          {t("fields.intro")}
        </p>
        {loading && defs.length === 0 ? (
          <div className="loading">{t("common.loading")}</div>
        ) : sortedDefs.length === 0 ? (
          <div className="empty-state" style={{ padding: "36px 20px" }}>
            <i className="fa-solid fa-sliders empty-state-icon" aria-hidden="true" />
            <div style={{ fontFamily: "var(--font-display)", fontSize: 19, color: "var(--ink)", marginBottom: 6 }}>
              {t("fields.empty_title")}
            </div>
            <p style={{ margin: 0 }}>{t("fields.empty_message")}</p>
          </div>
        ) : (
          <div className="field-cards">
            {sortedDefs.map((d) => (
              <article key={d.id} className={`field-card${editingId === d.id ? " field-card--editing" : ""}`}>
                <div className="field-card__order" title={t("fields.sort_order")}>
                  {d.sortOrder ?? 0}
                </div>
                <div className="field-card__body">
                  <div className="field-card__title">
                    {d.label}
                    <span className={`tag ${d.fieldType === "SELECT" ? "tag-info" : ""}`}>
                      {d.fieldType === "SELECT" ? t("fields.fixed_list") : t("fields.free_text")}
                    </span>
                  </div>
                  <code className="field-card__key">{d.fieldKey}</code>
                  {d.fieldType === "SELECT" && (d.options || []).length > 0 ? (
                    <div className="field-card__options">
                      {(d.options || []).slice(0, 8).map((o) => (
                        <span key={o} className="order-chip order-chip--more">
                          {o}
                        </span>
                      ))}
                      {(d.options || []).length > 8 ? (
                        <span className="order-chip order-chip--more">+{d.options.length - 8}</span>
                      ) : null}
                    </div>
                  ) : null}
                </div>
                <div className="field-card__actions">
                  <button type="button" className="icon-button" onClick={() => startEdit(d)} aria-label={t("catalog.edit_name", { name: d.label })} title={t("catalog.edit")}>
                    <i className="fa-solid fa-pen" aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    className="icon-button icon-button--danger"
                    onClick={() => remove(d.id)}
                    aria-label={t("catalog.delete_name", { name: d.label })}
                    title={t("catalog.delete")}
                  >
                    <i className="fa-regular fa-trash-can" aria-hidden="true" />
                  </button>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      <aside className="fields-layout__form">
        <form onSubmit={submit} className="panel store-section field-form">
          <h2 className="store-section__title">
            <i className={`fa-solid ${editingId ? "fa-pen" : "fa-plus"} store-section__icon`} aria-hidden="true" />
            {editingId ? t("fields.edit_field") : t("fields.add_a_field")}
          </h2>
          <div>
            <label className="field-label" htmlFor="pfd-label">
              {t("fields.display_label")}
            </label>
            <input
              id="pfd-label"
              value={form.label}
              onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
              placeholder={t("fields.label_placeholder")}
            />
          </div>
          {form.label.trim() || editingId ? (
            <div className="muted" style={{ fontSize: 12, marginTop: -6 }}>
              {t("fields.saved_as")}{" "}
              <code className="field-card__key">
                {editingId ? form.fieldKey : keyFromLabel(form.label, defs.map((d) => d.fieldKey))}
              </code>{" "}
              {t("fields.xlsx_column_note")}
            </div>
          ) : null}
          <div>
            <span className="field-label">{t("fields.field_type")}</span>
            <div className="segmented" role="radiogroup" aria-label={t("fields.field_type")}>
              <button
                type="button"
                role="radio"
                aria-checked={form.fieldType === "TEXT"}
                className={form.fieldType === "TEXT" ? "segmented__on" : ""}
                onClick={() => setForm((f) => ({ ...f, fieldType: "TEXT" }))}
              >
                {t("fields.free_text")}
              </button>
              <button
                type="button"
                role="radio"
                aria-checked={form.fieldType === "SELECT"}
                className={form.fieldType === "SELECT" ? "segmented__on" : ""}
                onClick={() => setForm((f) => ({ ...f, fieldType: "SELECT" }))}
              >
                {t("fields.fixed_list")}
              </button>
            </div>
          </div>
          {form.fieldType === "SELECT" ? (
            <div>
              <label className="field-label" htmlFor="pfd-options">
                {t("fields.options")} <span className="muted" style={{ fontWeight: 400 }}>{t("fields.one_per_line")}</span>
              </label>
              <textarea
                id="pfd-options"
                value={form.optionsText}
                onChange={(e) => setForm((f) => ({ ...f, optionsText: e.target.value }))}
                rows={4}
                placeholder={t("fields.options_placeholder")}
              />
            </div>
          ) : null}
          <div style={{ maxWidth: 140 }}>
            <label className="field-label" htmlFor="pfd-order">
              {t("fields.sort_order")}
            </label>
            <input
              id="pfd-order"
              type="number"
              value={form.sortOrder}
              onChange={(e) => setForm((f) => ({ ...f, sortOrder: e.target.value }))}
            />
          </div>
          {error ? (
            <div className="alert alert--error" style={{ margin: 0 }}>
              <i className="fa-solid fa-circle-exclamation" aria-hidden="true" /> {error}
            </div>
          ) : null}
          <div className="row" style={{ gap: 8 }}>
            <button type="submit" className="primary">
              {editingId ? t("store.save_changes") : t("fields.add_field")}
            </button>
            {editingId ? (
              <button type="button" onClick={resetForm}>
                {t("common.cancel")}
              </button>
            ) : null}
          </div>
        </form>
      </aside>
    </div>
  );
}
